import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "@/core/api";
import { THIRTY_MINUTES } from "@/core/utils/time";
import { REFERRAL_STATUS } from "@/referrals/constants";
import { referralsKey } from "@/referrals/query";
import { IReferral } from "@/referrals/referral";
import { PAYOUT_STATUS } from "./constants";
import { InitiateVerificationResponse, IPayout } from "./payouts";
import { getEnrichedPayout } from "./utils";

export const payoutsKey = (businessId?: string | null) =>
  ["payouts", businessId] as const;
export const payoutByEndpointKey = (payoutEndpoint?: string) =>
  ["payout-by-endpoint", payoutEndpoint] as const;
export const initiateVerificationKey = (payoutEndpoint?: string) =>
  ["initiate-verification", payoutEndpoint] as const;

export interface SendPayoutParams {
  amount: number;
  final_payout: boolean;
  notes?: string;
  customer_message?: string;
}

export const useSendPayout = (referralId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation({
    mutationFn: async (params: SendPayoutParams) => {
      const { data } = await api.post("/payout_funds/send", {
        referral_id: referralId,
        ...params,
      });

      return data;
    },
    onSuccess: (data, { final_payout }) => {
      const businessId = data.referral.business_id;
      queryClient.setQueryData(
        referralsKey(businessId),
        (referrals: IReferral[]) =>
          referrals.map((referral) =>
            referral.id === referralId
              ? {
                  ...referral,
                  status: final_payout ? REFERRAL_STATUS.PAID : referral.status,
                  payouts: [...referral.payouts, data],
                }
              : referral,
          ),
      );
    },
  });
};

export const useGetPayouts = <TData = IPayout[]>(
  businessId?: string | null,
  select?: (data: IPayout[]) => TData,
) => {
  const api = useApi();

  return useQuery({
    queryKey: payoutsKey(businessId),
    queryFn: (): Promise<IPayout[]> =>
      api
        .get(`/payouts?business_id=${businessId}`)
        .then(({ data }) => data.map(getEnrichedPayout)),
    enabled: !!businessId,
    select,
  });
};

export const useGetPayout = (
  businessId?: string | null,
  payoutId?: string | null,
) =>
  useGetPayouts(
    businessId,
    useCallback(
      (payouts: IPayout[]) => payouts.find((p) => p.id === payoutId) || null,
      [payoutId],
    ),
  );

export const useUpdatePayout = (businessId?: string | null) => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation({
    mutationFn: (payout: Partial<IPayout>) =>
      api.put(`/payouts/${payout.id}`, payout),
    onSuccess: (response) => {
      const enrichedPayout = getEnrichedPayout(response.data);
      queryClient.setQueryData(payoutsKey(businessId), (payouts: IPayout[]) =>
        payouts?.map((payout) =>
          payout.id === enrichedPayout.id ? enrichedPayout : payout,
        ),
      );
    },
  });
};

export const useGetPayoutByEndpoint = (payoutEndpoint?: string) => {
  const api = useApi();

  return useQuery({
    queryKey: payoutByEndpointKey(payoutEndpoint),
    queryFn: () =>
      api
        .get(`/payouts/endpoint/${payoutEndpoint}`)
        .then(({ data }) => getEnrichedPayout(data as IPayout)),
    enabled: !!payoutEndpoint,
  });
};

export const useGetInitiateVerification = (payoutEndpoint?: string) => {
  const api = useApi();
  const { data: payoutData } = useGetPayoutByEndpoint(payoutEndpoint);

  const result = useQuery({
    queryKey: initiateVerificationKey(payoutEndpoint),
    queryFn: async (): Promise<InitiateVerificationResponse> => {
      const storageKey = `payout-verification-${payoutEndpoint}`;
      const storedData = localStorage.getItem(storageKey);

      // Use the stored data if it exists and is less than 30 minutes old
      if (storedData) {
        const parsedData = JSON.parse(
          storedData,
        ) as InitiateVerificationResponse;
        const now = Date.now();

        if (
          parsedData.timestamp &&
          now - parsedData.timestamp < THIRTY_MINUTES
        ) {
          return parsedData;
        }
      }

      const { data } = await api.get(
        `/payouts/${payoutEndpoint}/initiate_verification`,
      );

      const dataWithTimestamp = {
        ...data,
        timestamp: Date.now(),
      };
      localStorage.setItem(storageKey, JSON.stringify(dataWithTimestamp));

      return dataWithTimestamp;
    },
    // Only send if the payout is not claimed or paid out
    enabled:
      !!payoutData &&
      ![PAYOUT_STATUS.CREDIT_CLAIMED, PAYOUT_STATUS.PAID_OUT].includes(
        // @ts-expect-error - status is not typed
        payoutData.status,
      ),
    retry: false,
    staleTime: Infinity, // Prevent auto-refetching
  });

  const forceRefetch = useCallback(() => {
    if (payoutEndpoint) {
      // Clear the localStorage entry to force a new API call
      localStorage.removeItem(`payout-verification-${payoutEndpoint}`);
      // Now refetch will make a new API call because there's no data in localStorage
      return result.refetch();
    }
    return Promise.resolve();
  }, [payoutEndpoint, result.refetch]);

  return {
    ...result,
    refetch: forceRefetch,
  };
};

// Utility function to clean up localStorage when a payout is claimed
export const cleanupPayoutVerification = (payoutEndpoint?: string) => {
  if (payoutEndpoint) {
    localStorage.removeItem(`payout-verification-${payoutEndpoint}`);
  }
};

export const useVerifyCode = (payoutEndpoint?: string) => {
  const api = useApi();

  return useMutation({
    mutationFn: (sms_code: string) =>
      api.post(`/payouts/${payoutEndpoint}/verify_code`, { sms_code }),
  });
};

export const useClaimCredit = (payoutEndpoint?: string) => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation({
    mutationFn: () => api.post(`/payouts/${payoutEndpoint}/claim_credit`),
    onSuccess: () => {
      queryClient.setQueryData(
        payoutByEndpointKey(payoutEndpoint),
        (payout: IPayout) =>
          getEnrichedPayout({
            ...payout,
            status: PAYOUT_STATUS.CREDIT_CLAIMED,
          }),
      );
    },
  });
};
